import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, DatePicker, Input, Row, Select, Switch, Tooltip } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, get, isEmpty, toLower } from "lodash";
import { useFormik } from "formik";
import { setSection } from "../../../redux/ducks/Section";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import EditableTagGroup from "../../../components/hooks_components/EditableTagGroup/EditableTagGroup";
import { dbDate, langDate } from "../../../utils/properties";
import { checkDate, passwordStrenght, showError, showSuccess, trimData } from "../../../utils/utilities";
import PasswordStrenght from "../../../components/functional_components/PasswordStrenght/PasswordStrenght";
import Button from "../../../components/functional_components/Button/PFButton";
import User from "../../../services/api/User";
import { setLoading } from "../../../redux/ducks/Loading";
import Supplier from "../../../services/api/Supplier";
import dayjs from "dayjs";

const { Option } = Select;

const UserSupplierDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const dispatch = useDispatch();
  const isNewSupplier = params.supplierUsername === "new-supplier";

  const [state, setState] = useState({
    currentSupplierData: {},
    suppliersOptionsList: [],
    saveIsLoading: false,
  });

  useEffect(() => {
    getSupplierData();
    dispatch(
      setSection({
        section: {
          title: params.supplierUsername === "new-supplier" ? "userSupplier.newSupplier" : params.supplierUsername,
        },
      })
    );
  }, []);

  const getSupplierData = async () => {
    let currentSupplierData = null;
    if (!isNewSupplier) {
      dispatch(setLoading(true));
      currentSupplierData = await User.getUserData(params.supplierUsername);
      if (!currentSupplierData.error && currentSupplierData.data) {
        setState((prevState) => ({ ...prevState, currentSupplierData: currentSupplierData.data }));
      } else {
        showError(get(currentSupplierData, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    }
  };

  const formikDetailSupplier = useFormik({
    // id, name and type refers to product category ID, description and type
    initialValues: {
      username: get(state.currentSupplierData, "username", ""),
      password: get(state.currentSupplierData, "password", ""),
      nominative: get(state.currentSupplierData, "nominative", ""),
      name: get(state.currentSupplierData, "supplier.name", null),
      id: get(state.currentSupplierData, "supplier.id", ""),
      emailList: get(state.currentSupplierData, "emailList", []),
      dateActivityStart:
        get(state.currentSupplierData, "dateActivityStart", null) !== null
          ? dayjs(state.currentSupplierData.dateActivityStart, dbDate).format(dateFormat)
          : null,
      dateActivityEnd:
        get(state.currentSupplierData, "dateActivityEnd", null) !== null
          ? dayjs(state.currentSupplierData.dateActivityEnd, dbDate).format(dateFormat)
          : null,
      enabled: get(state.currentSupplierData, "enabled", false),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.username) {
        errors.username = "Required!";
      }
      if (
        (isNewSupplier && passwordStrenght(values.password) !== 100) ||
        (!isNewSupplier && values.password !== "" && passwordStrenght(values.password) !== 100)
      ) {
        errors.password = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      if (isEmpty(values.emailList)) {
        errors.emailList = "Required!";
      }
      if (
        values.dateActivityStart &&
        values.dateActivityEnd &&
        checkDate(values.dateActivityStart, values.dateActivityEnd, dateFormat)
      ) {
        errors.dateActivityStart = "Required!";
        errors.dateActivityEnd = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values.dateActivityEnd) {
        values.dateActivityEnd = dayjs(values.dateActivityEnd, dateFormat).format(dbDate);
      } else {
        values.dateActivityEnd = null;
      }
      if (values.dateActivityStart) {
        values.dateActivityStart = dayjs(values.dateActivityStart, dateFormat).format(dbDate);
      } else {
        values.dateActivityStart = null;
      }
      submitUserSupplier(values);
    },
  });

  const submitUserSupplier = async (values) => {
    setState((prevState) => ({ ...prevState, saveIsLoading: true }));
    const valuesClone = cloneDeep(values);
    /**
     *refactor of values in order to add the supplier and type properties and in order to move "id" and "name"
     * into a "supplier" object, as requested from backend
     **/
    valuesClone.supplier = {
      id: valuesClone.id,
      name: valuesClone.name,
    };
    valuesClone.roles = ["SUPPLIER"];
    valuesClone.type = "SUPPLIER";
    delete valuesClone.id;
    delete valuesClone.name;
    const submitResponse = await User.saveUserSupplier(valuesClone);
    if (!submitResponse.error && submitResponse.data) {
      showSuccess();
      navigate("/user-suppliers");
    } else {
      showError(get(submitResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, saveIsLoading: false }));
  };

  const onSearchBusinessName = async (value) => {
    if (value.length >= 3) {
      const suppliersWithoutUser = await Supplier.getSuppliersWithoutUser(`filter=${value}`);
      if (!suppliersWithoutUser.error && suppliersWithoutUser.data) {
        setState((prevState) => ({ ...prevState, suppliersOptionsList: suppliersWithoutUser.data }));
      } else {
        showError(get(suppliersWithoutUser, "error.messageCode", "ER000"));
      }
    } else {
      setState((prevState) => ({ ...prevState, suppliersOptionsList: [] }));
    }
  };

  const changeBusinessName = (value, element) => {
    formikDetailSupplier.setFieldValue("name", element.name);
    formikDetailSupplier.setFieldValue("id", element.value);
  };

  const emailValidation = (email) => {
    let error = null;
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i.test(email)) {
      error = t("userSupplier.invalidEmailAddress");
    }
    return error;
  };

  return (
    <div className="padding-30">
      <Row gutter={[48, 32]}>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold" htmlFor="username">
              {t("general.Username")}*
            </label>
          </Row>
          <Row>
            <Input
              disabled={!isNewSupplier}
              id="username"
              name="username"
              placeholder={t("general.Username")}
              onChange={formikDetailSupplier.handleChange}
              onBlur={formikDetailSupplier.handleBlur}
              value={formikDetailSupplier.values.username}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <Col span={12}>
              <label className="fw-bold" htmlFor="password">
                {t("general.Password")}
                {isNewSupplier ? "*" : ""}
              </label>
            </Col>
            <Col span={12} className="right">
              <Tooltip
                title={
                  <div>
                    <p>{t("userSupplier.passwordRequirements.minLenght")}</p>
                    <ul>
                      <li>{t("userSupplier.passwordRequirements.minChar")}</li>
                      <li>{t("userSupplier.passwordRequirements.maxChar")}</li>
                      <li>{t("userSupplier.passwordRequirements.number")}</li>
                      <li>{t("userSupplier.passwordRequirements.specialChar")}</li>
                    </ul>
                  </div>
                }
              >
                <InfoCircleOutlined className="icon-small" />
              </Tooltip>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Input.Password
                placeholder={t("general.Password")}
                id="password"
                name="password"
                onChange={formikDetailSupplier.handleChange}
                onBlur={formikDetailSupplier.handleBlur}
                value={formikDetailSupplier.values.password}
              />
            </Col>
            <Col span={24}>
              <PasswordStrenght password={formikDetailSupplier.values.password} />
            </Col>
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold" htmlFor="nominative">
              {t("userSupplier.referentName")}
            </label>
          </Row>
          <Row>
            <Input
              id="nominative"
              name="nominative"
              placeholder={t("userSupplier.referentName")}
              onChange={formikDetailSupplier.handleChange}
              onBlur={formikDetailSupplier.handleBlur}
              value={formikDetailSupplier.values.nominative}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="name">
              {t("userSupplier.name")}*
            </label>
          </Row>
          <Row>
            <Select
              disabled={!isNewSupplier}
              className="w-100"
              id="name"
              name="name"
              showSearch
              placeholder={t("userSupplier.name")}
              optionFilterProp="children"
              onChange={changeBusinessName}
              onBlur={formikDetailSupplier.handleBlur}
              onSearch={onSearchBusinessName}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              value={formikDetailSupplier.values.name}
            >
              {state.suppliersOptionsList.length > 0 &&
                state.suppliersOptionsList.map((supplier) => {
                  return (
                    <Option key={supplier.id} value={supplier.id} name={supplier.name}>
                      {supplier.name}
                    </Option>
                  );
                })}
            </Select>
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="id">
              {t("userSupplier.id")}*
            </label>
          </Row>
          <Row>
            <Input
              id="id"
              name="id"
              placeholder={t("userSupplier.id")}
              onChange={formikDetailSupplier.handleChange}
              onBlur={formikDetailSupplier.handleBlur}
              value={formikDetailSupplier.values.id}
              disabled={true}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="emailList">
              {t("userSupplier.emailList")}*
            </label>
          </Row>
          <Row>
            <EditableTagGroup
              tags={formikDetailSupplier.values.emailList}
              tagsInputConfirm={(tags) => {
                formikDetailSupplier.setFieldValue("emailList", tags);
              }}
              title={t("userSupplier.enterEmail")}
              validation={(value) => emailValidation(value)}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="dateActivityStart">
              {t("userSupplier.dateActivityStart")}
            </label>
          </Row>
          <Row>
            <DatePicker
              id="dateActivityStart"
              name="dateActivityStart"
              format={dateFormat}
              locale={langDate[`locale_${toLower(currentLanguage)}`]}
              value={
                formikDetailSupplier.values.dateActivityStart
                  ? dayjs(formikDetailSupplier.values.dateActivityStart, dateFormat)
                  : undefined
              }
              onChange={(date, dateString) => {
                formikDetailSupplier.setFieldValue("dateActivityStart", dateString);
              }}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="dateActivityEnd">
              {t("userSupplier.dateActivityEnd")}
            </label>
          </Row>
          <Row>
            <DatePicker
              id="dateActivityEnd"
              name="dateActivityEnd"
              format={dateFormat}
              locale={langDate[`locale_${toLower(currentLanguage)}`]}
              value={
                formikDetailSupplier.values.dateActivityEnd
                  ? dayjs(formikDetailSupplier.values.dateActivityEnd, dateFormat)
                  : undefined
              }
              onChange={(date, dateString) => {
                formikDetailSupplier.setFieldValue("dateActivityEnd", dateString);
              }}
            />
          </Row>
        </Col>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="enabled">
              {t("general.enabled")}
            </label>
          </Row>
          <Row>
            <Switch
              id="enabled"
              name="enabled"
              onChange={() => {
                formikDetailSupplier.setFieldValue("enabled", !formikDetailSupplier.values.enabled);
              }}
              checked={formikDetailSupplier.values.enabled}
            />
          </Row>
        </Col>
      </Row>
      <Row type="flex" align="middle" justify="end" className="mt-10">
        <Button
          currentSize="large"
          isLoading={state.saveIsLoading}
          isDisabled={!(formikDetailSupplier.dirty && formikDetailSupplier.isValid)}
          clickCallback={formikDetailSupplier.submitForm}
          currentIcon={<SaveOutlined className="icon-large" />}
        />
      </Row>
    </div>
  );
};

export default UserSupplierDetail;
